import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

// Constants
const RETRY_INCREMENT = 2000;
const RETRY_MAX_SECONDS = 60;
const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const IDEMPOTENT_METHODS = ['POST', 'PUT', 'DELETE'];
const IDEMPOTENT_HEADER = 'x-abtra-idempotency-key';

export default function useIdempotency() {
    // Execute a request with idempotency
    const execute = useCallback(async (url, body = null, method = 'POST', headers = {}) => {
        // Generate a new idempotency key
        const idempotencyKey = uuidv4();

        // Ensure the method is idempotent
        if (!IDEMPOTENT_METHODS.includes(method)) {
            return;
        }

        // Initialize variables
        let isDone = false;
        let error = null;
        let retryDelay = 0;

        // Retry the request with incremental backoff
        while (retryDelay < RETRY_MAX_SECONDS) {
            if (isDone) {
                break;
            }
            try {
                const response = await fetch(url, {
                    method,
                    headers: {
                        ...headers,
                        [IDEMPOTENT_HEADER]: idempotencyKey,
                    },
                    ...body !== null && ({ body: JSON.stringify(body) }),
                });
                if (response.ok) {
                    isDone = true;
                    const statusCode = response.status;
                    if (statusCode === 204) {
                        return null;
                    }
                    const contentType = response.headers.get('content-type');
                    // If content type is JSON, parse the response
                    if (contentType
                        && contentType.includes('application/json')) {
                        const data = await response.json();
                        if (data.error) {
                            throw new Error(data.error);
                        }
                        return data;
                    }
                    // If no content type, return the response text
                    return response.text();
                }
                throw new Error(DEFAULT_ERROR_MESSAGE);
            } catch (e) {
                error = e.message;
                console.error(e);
                retryDelay += RETRY_INCREMENT;
                await new Promise((resolve) => setTimeout(resolve, retryDelay));
            }
        }

        // Handle errors
        if (!isDone) {
            if (error) {
                throw new Error(error);
            }
            throw new Error(DEFAULT_ERROR_MESSAGE);
        }
    }, []);

    return { execute };
}